import axios from 'axios';
import { getToken, logout, shouldRefreshToken, setToken, setUser } from '../utils/auth';

// Base API configuration
export const baseApiConfig = {
  baseURL: process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:7108/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
};

// Create a base axios instance
export const createApiInstance = (additionalConfig = {}) => {
  const instance = axios.create({
    ...baseApiConfig,
    ...additionalConfig,
  });

  // Add request interceptor for JWT token, auto-refresh, and debugging
  instance.interceptors.request.use(
    async config => {
      // Check if token needs refresh before making the request
      if (shouldRefreshToken()) {
        try {
          const response = await axios.post(
            `${baseApiConfig.baseURL}/refresh`,
            {},
            {
              headers: {
                Authorization: `Bearer ${getToken()}`,
              },
            }
          );

          // Update tokens
          setToken(response.data.access_token);
          setUser(response.data.user);
        } catch (error) {
          console.error('Token refresh failed:', error);
          // If refresh fails, logout user
          logout();
          return Promise.reject(error);
        }
      }

      // Add JWT token to all requests
      const token = getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      return config;
    },
    error => {
      console.error('API Request Error:', error);
      return Promise.reject(error);
    }
  );

  // Add response interceptor for debugging and token handling
  instance.interceptors.response.use(
    response => {
      return response;
    },
    error => {
      console.error('API Response Error:', {
        url: error.config?.url,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      });

      // Handle unauthorized responses (expired/invalid token)
      if (error.response?.status === 401) {
        logout();
      }

      return Promise.reject(error);
    }
  );

  return instance;
};

// Default instance
export const baseApi = createApiInstance();
